/**
 * Convert HTML Entities to symbols (of the format `&#...;`)
 * 
 * @param str
 * @return string
 */
 export const decodeHtmlEntities = (str: string): string => 
 {
     return str.replace(/&#(\d+);/g, (match, dec) => {
         return String.fromCharCode(dec);
     });
 };
 
 /**
  * Large Number Symbol Map
  */
 const lnsm = [
     { value: 1E6, symbol: "m" },
     { value: 1E9, symbol: "bn" },
     { value: 1E12, symbol: "tn" },
     { value: 1E15, symbol: "p" },
     { value: 1E18, symbol: "e" }
 ];
 
 /**
  * Format a number with thousands and decimals separators
  * 
  * @param value The number to be formatted
  * @param decimals Number of decimal places
  * @param thousandsSeparator Character to use as thousands separator, default: `,`
  * @returns string
  */
 export const formatNumber = (value: number, decimals: number, reduceLargeNumber: boolean = false, thousandsSeparator: string = ',', decimalPoint: string = '.'): string =>
 {
     if ( value === undefined ) {
         return '';
     }
     // determine if negative
     const negative: boolean = value < 0;
     // if negative multiply by -1 to obtain a positive number
     // the - symbol we be re-added later
     if ( negative ) value = value * -1;
     // find the size of multiplier needed for this many decimal places
     let multiplier: number = Math.pow(10, decimals);
     // round the number and split by the decimal point
     var parts = (Math.round(value * multiplier) / multiplier).toFixed(decimals).split('.');
     if ( true === reduceLargeNumber && parts[0].length > 6 ) {
         // this is a large number, and large numbers have been requested to be reduced
         let i: number;
         for ( i = lnsm.length - 1; i > 0; i-- ) {
             if ( value >= lnsm[i].value) {
                 break;
             }
         }
         return `${negative ? '-':''}${(value / lnsm[i].value).toFixed(decimals)} ${lnsm[i].symbol}`;
     }
     // add thousands separator(s)
     parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
     
     return (negative ? '-':'') + parts.join(decimalPoint);
 };
 
 /**
  * @param value Formats a number with thousand separators and decimal places based on the size of the number
  * @param locale defaults to 'en-GB'
  * @returns The input value formatted as a string
  */
 export const formatNumberAuto = (value: number, locale: string = 'en-GB'): string => {
    const valueAbs: number = Math.abs(value);
    let max: number = 2;
    if (valueAbs < 10) max = 5;
    else if (valueAbs < 100) max = 4;
    else if (valueAbs < 1000) max = 3;
    return value.toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: max
    });
 }
 
 /**
  * default DateTimeFormatOptions
  */
 const defaultDateTimeFormatOptions: Intl.DateTimeFormatOptions = {
     year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric'
 };
 
 /**
  * Format a date string returned from GQL API for JS Date object
  * NOTE: this will be deprecated once GQL API date strings are standardised
  * 
  * @param date string to be tested for JS Date validity
  */
 export const formatRawDate = (date: string): string =>
 {
     try {
         formatDateTime(date);
         return date;
     }
     catch (e) {
         const dateParts = date.split("-");
         if ( dateParts[2].length == 4 ) {
             return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
         }
         else if ( dateParts[0].length == 4 ) {
             return `${dateParts[0]}/${dateParts[1]}/${dateParts[2]}`;
         }
         else {
             throw 'Cannot detect date formatting';
         }
     }
 };
 
 /**
  * Format a date string
  * 
  * @param dateTime DateTime string to be formatted
  * @param format Formatting options
  * @returns string
  */
 export const formatDateTime = (dateTime: string, format: Intl.DateTimeFormatOptions = defaultDateTimeFormatOptions): string => 
 {
     return new Intl.DateTimeFormat(
         'en-GB',
         format
     ).format(new Date(dateTime));
 };
 
 /**
  * Curreny data for use with `getSymbol` and `formatPrice` methods below
  */
 export const currencyData: { [key: string]: any } = {
     pence: {
         symbol: 'p',
         position: 1,
         parent: 'GBP',
         parentRatio: 100
     },
     GBX: {
         symbol: 'p',
         position: 1,
         parent: 'GBP',
         parentRatio: 100
     },
     GBP: {
         symbol: '&#163;',
         position: 0
     },
     USD: {
         symbol: '&#36;',
         position: 0
     },
     USX: {
         symbol: 'c',
         position: 1,
         parent: 'USD',
         parentRatio: 100
     },
     EUR: {
         symbol: '&#8364;',
         position: 0
     },
     EUX: {
         symbol: 'c',
         position: 1,
         parent: 'EUR',
         parentRatio: 100
     },
     JPY: {
         symbol: '&#165;',
         position: 0
     }
 };
 
 export const relatedCurrencies = ( currencyRef1: string, currencyRef2: string ): boolean =>
 {
     return (
         // both currencies must be valid
         (currencyData.hasOwnProperty(currencyRef1) && currencyData.hasOwnProperty(currencyRef2)) &&
         // AND
         // they are the same 
         currencyRef1 == currencyRef2 || 
         // OR currencyRef2 is the parent of currencyRef1
         currencyData[currencyRef1]?.parent == currencyRef2 ||
         // OR currencyRef1 is the parent of currencyRef2
         currencyData[currencyRef2]?.parent == currencyRef1 
     );
 };
 
 /**
  * Check if a current has a parent
  * 
  * @param currencyRef 
  */
 export const hasParentCurrency = (currencyRef: string): boolean =>
 {
     return (currencyData.hasOwnProperty(currencyRef) && currencyData[currencyRef].hasOwnProperty('parent'));
 };
 
 /**
  * Get the parent currency if it exists
  * 
  * @param currencyRef 
  */
 export const getParentCurrency = (currencyRef: string) =>
 {
     if ( hasParentCurrency(currencyRef) ) {
         return currencyData[currencyRef].parent;
     }
 
     return null;
 };
 
 /**
  * Convert a value to parent currency (if it exists) using defined ratio
  * 
  * @param currencyRef 
  * @param amount 
  */
 export const convertAmountToParentCurrency = (currencyRef: string, amount: number): number =>
 {
     if ( hasParentCurrency(currencyRef) ) {
         // parent currency detected, fetch the ratio and convert
         const parentRatio = currencyData[currencyRef].parentRatio;
         return amount / parentRatio;
     }
     // does not have a parent currency, return initial value
     return amount;
 };
 
 /**
  * Convert a value to child currency (if it exists) using defined ratio
  * 
  * @param currencyRef 
  * @param amount 
  */
 export const convertAmountToChildCurrenncy = ( currencyRef: string, amount: number ): number =>
 {
     for ( let k in currencyData ) {
         const curData = currencyData[k];
         if ( curData.hasOwnProperty('parent') && curData.parent == currencyRef ) {
             const parentRatio = curData.parentRatio;
             return amount * parentRatio;
         }
     }
     // does not have a child currency, return initial value
     return amount;
 };
 
 /**
  * Get HTML Entity reference for requested currency
  * A value of `true` for `returnMain` will check if the currency has a parent, eg `GBP` is the parent of `GBX`
  * 
  * @param currencyRef Currency as 3 character reference, eg `GBP`
  * @param returnMain Whether to return the parent currency, eg `GBP` is the parent of `GBX`. Default: `false`
  * @returns string
  */
 export const getCurrencySymbol = (currencyRef: string, returnMain: boolean = false): string => 
 {
     // ensure the currency reference is present
     if ( !currencyData.hasOwnProperty(currencyRef) ) {
         // not present - return original currency reference
         return currencyRef;
     }
     // check if seeking 'parent' currency
     if ( returnMain && hasParentCurrency(currencyRef) ) {
         return getCurrencySymbol(currencyData[currencyRef].parent);
     }
     // return symbol
     return currencyData[currencyRef].symbol;
 };
 
 /**
  * Format a number as price, will append/prepend the requested currency symbol
  *  
  * @param currencyRef Currency as 3 character reference, eg `GBP`
  * @param amount String value of amount - pre-format the number to string
  * @returns string
  */
 export const formatPrice = (currencyRef: string, amount: string|number, parentCurrency: boolean = false): string => 
 {
     if ( amount === undefined || amount === null || amount == '' ) {
         return '';
     }
     if ( typeof amount == 'string' ) {
         amount = parseFloat(amount);
     }
     const amountFormatted: string = formatNumberAuto(amount);
     // ensure the currency reference is present
     if ( !currencyData.hasOwnProperty(currencyRef) ) {
         // not present, return amount with appended currency reference
         return `${amountFormatted}\u202F${currencyRef}`;
     }
     // check to see if formatting is requested as the parentCurrency
     // and if so that the currencyRef has a parentCurrency listed
     if ( true === parentCurrency && hasParentCurrency(currencyRef) ) {
         // parent currency is present, recall the function using the parentCurrency ref.
         return formatPrice(currencyData[currencyRef].parent, amount);
     }
     // get symbol
     const symbol = getCurrencySymbol(currencyRef, parentCurrency);
     // return combined symbol with amount in correct positions
     if ( currencyData[currencyRef].position === 0 ) {
         // symbol position is to the left, ensure correct positioning
         // of minus symbol if amount is negative
         if ( amount < 0 ) {
             // amount is negative, ensure the minus symbol is outside currency symbol
             return decodeHtmlEntities(`-${symbol}${amountFormatted.replace('-','')}`);
         }
         return decodeHtmlEntities(`${symbol}${amountFormatted}`);
     }
     else {
         return decodeHtmlEntities(`${amountFormatted}${symbol}`);
     }
 };

 /**
  * @todo: review & refactor, this is a crude POC
  * @param originalAmount 
  * @param instrumentCurrency 
  * @param streamingCurrency 
  * @returns 
  */
export const normalizeStreamingPrice = (streamingPrice, instrumentCurrency: string,  streamingCurrency: string) => {
    const instrumentCurrencyGBX = 'GBX';
    const instrumentCurrencyGBP = 'GBP';
    const streamingCurrencyGBX1 = '1';
    const streamingCurrencyGBX2 = '13';
    const streamingCurrencyGBP = '28';

    let price = streamingPrice;
    if (instrumentCurrency === instrumentCurrencyGBX) {
        if (streamingCurrencyGBP == streamingCurrency) {
            price = (price / 100).toFixed(4).replace(/0+$/, "");
        }
    } else if (instrumentCurrency === instrumentCurrencyGBP) {
        if ([streamingCurrencyGBX1, streamingCurrencyGBX2].some(x => x == streamingCurrency)) {
            price = price / 100;
        }
    }
    return price;
 }

 /**
  * @todo: review & refactor, this is a crude POC
  * @param referenceValue - instrument price or other property
  * @param referenceCurrency  - instrument currency
  * @param streamingValue - streaming price or other property
  * @param streamingCurrency 
  * @returns string
  */
export const formatStreamingValue = (referenceValue, referenceCurrency, streamingValue, streamingCurrency): string => {
    const instrumentPriceDecimals = referenceValue.toString().split('.')[1]?.length;
    const decimalsCount = Math.max(Math.min(instrumentPriceDecimals || 2, 3), 2); // Min 2 characters
    const normalized = normalizeStreamingPrice(streamingValue, referenceCurrency, streamingCurrency);
    const formattedNumber = formatNumber(normalized, decimalsCount, false, '');

    return formatPrice(referenceCurrency, formattedNumber)
 }
 
 /**
  * Generate change classname(s) for text
  * @param changeNum
  */
 export const changeClassnameText = (changeNum: number): string =>
 {
     let result: string = '';
     if (changeNum > 0) {
         result = 'stock-up';
     }
     else if ( changeNum < 0 ) {
         result = 'stock-down';
     }
     return result;
 };
 /**
  * Generate change classname(s) for icon
  * @param changeNum
  */
 export const changeClassnameIcon = (changeNum: number): string =>
 {
     return `fas fa-xs fa-angle-${changeNum > 0 ? 'up':'down'}`;
 };